"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

/* ── Types ── */
export type RecentlyViewedItem = {
  name: string;
  generic: string;
  href: string;
  image: string;
  startingPrice: number;
  category: string;
  viewedAt: number; // timestamp
};

type RecentlyViewedContextValue = {
  items: RecentlyViewedItem[];
  addItem: (item: Omit<RecentlyViewedItem, "viewedAt">) => void;
  clearAll: () => void;
};

const STORAGE_KEY = "bp-recently-viewed";
const MAX_ITEMS = 10;

const RecentlyViewedContext = createContext<RecentlyViewedContextValue | null>(null);

/* ── Helpers ── */
function loadItems(): RecentlyViewedItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as RecentlyViewedItem[]) : [];
  } catch {
    return [];
  }
}

function saveItems(items: RecentlyViewedItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* silently ignore */
  }
}

/* ── Provider ── */
export function RecentlyViewedProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<RecentlyViewedItem[]>([]);

  useEffect(() => {
    setItems(loadItems());
  }, []);

  const addItem = useCallback(
    (item: Omit<RecentlyViewedItem, "viewedAt">) => {
      setItems((prev) => {
        const filtered = prev.filter((i) => i.href !== item.href);
        const updated = [{ ...item, viewedAt: Date.now() }, ...filtered].slice(
          0,
          MAX_ITEMS,
        );
        saveItems(updated);
        return updated;
      });
    },
    [],
  );

  const clearAll = useCallback(() => {
    setItems([]);
    saveItems([]);
  }, []);

  return (
    <RecentlyViewedContext.Provider value={{ items, addItem, clearAll }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export function useRecentlyViewed() {
  const ctx = useContext(RecentlyViewedContext);
  if (!ctx)
    throw new Error(
      "useRecentlyViewed must be used within RecentlyViewedProvider",
    );
  return ctx;
}
